'use client';

import { useEffect, useRef, useState } from 'react';
import { consumeToolUsage } from '@/lib/browser-tool-usage';

type Variant =
  | 'sound-test'
  | 'speaker-test'
  | 'headphone-test'
  | 'left-right'
  | 'tone-generator'
  | 'hearing-test';

// WAVE / Web Audio discrete channel order.
const SURROUND: Record<number, string[]> = {
  2: ['Left', 'Right'],
  4: ['Front left', 'Front right', 'Rear left', 'Rear right'],
  6: ['Front left', 'Front right', 'Center', 'Subwoofer (LFE)', 'Surround left', 'Surround right'],
  8: [
    'Front left',
    'Front right',
    'Center',
    'Subwoofer (LFE)',
    'Rear left',
    'Rear right',
    'Side left',
    'Side right',
  ],
};

let shared: AudioContext | null = null;
function audio(): AudioContext {
  if (!shared || shared.state === 'closed') {
    const Ctx =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    shared = new Ctx();
  }
  if (shared.state === 'suspended') void shared.resume();
  return shared;
}

function pinkNoise(ctx: AudioContext, seconds: number): AudioBuffer {
  // Paul Kellet's economy pink-noise filter: equal energy per octave, easier on the ears
  // than white noise and the standard for speaker level checks.
  const buf = ctx.createBuffer(1, Math.floor(ctx.sampleRate * seconds), ctx.sampleRate);
  const d = buf.getChannelData(0);
  let b0 = 0;
  let b1 = 0;
  let b2 = 0;
  for (let i = 0; i < d.length; i += 1) {
    const w = Math.random() * 2 - 1;
    b0 = 0.99765 * b0 + w * 0.099046;
    b1 = 0.963 * b1 + w * 0.2965164;
    b2 = 0.57 * b2 + w * 1.0526913;
    d[i] = (b0 + b1 + b2 + w * 0.1848) * 0.11;
  }
  // 20 ms fades so bursts start and stop without clicks.
  const fade = Math.floor(ctx.sampleRate * 0.02);
  for (let i = 0; i < fade; i += 1) {
    d[i] *= i / fade;
    d[d.length - 1 - i] *= i / fade;
  }
  return buf;
}

/** A short three-note chime, panned: -1 left, 0 centre, 1 right. */
function chime(ctx: AudioContext, pan: number, invertRight = false) {
  const out = ctx.createGain();
  out.gain.value = 0.25;
  out.connect(ctx.destination);
  [523.25, 659.25, 783.99].forEach((f, i) => {
    const t = ctx.currentTime + i * 0.18;
    const o = ctx.createOscillator();
    o.type = 'sine';
    o.frequency.value = f;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(1, t + 0.02);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.5);
    o.connect(g);
    if (invertRight) {
      // Out-of-phase: identical signal, right channel polarity flipped.
      const merger = ctx.createChannelMerger(2);
      const inv = ctx.createGain();
      inv.gain.value = -1;
      g.connect(merger, 0, 0);
      g.connect(inv);
      inv.connect(merger, 0, 1);
      merger.connect(out);
    } else {
      const p = ctx.createStereoPanner();
      p.pan.value = pan;
      g.connect(p);
      p.connect(out);
    }
    o.start(t);
    o.stop(t + 0.55);
  });
}

function PlayButton({
  label,
  playing,
  onClick,
}: {
  label: string;
  playing: string | null;
  onClick: () => void;
}) {
  return (
    <button
      aria-pressed={playing === label}
      className={playing === label ? 'button' : 'button button-secondary'}
      onClick={onClick}
      type="button"
    >
      {playing === label ? `▶ ${label}` : label}
    </button>
  );
}

export default function BrowserSoundTest({
  config,
}: {
  config?: { toolSlug?: string; variant?: string; heading?: string };
}) {
  const variant = (config?.variant ?? 'sound-test') as Variant;
  const [playing, setPlaying] = useState<string | null>(null);
  const [channels, setChannels] = useState<number | null>(null);
  const [freq, setFreq] = useState(440);
  const [wave, setWave] = useState<OscillatorType>('sine');
  const [vol, setVol] = useState(0.2);
  const [bal, setBal] = useState(0);
  const [sweepHz, setSweepHz] = useState<number | null>(null);
  const [heard, setHeard] = useState<{ high?: number; low?: number }>({});
  const stopRef = useRef<(() => void) | null>(null);
  const toneRef = useRef<{ o: OscillatorNode; g: GainNode; p: StereoPannerNode } | null>(null);
  const reported = useRef(false);

  const report = () => {
    if (reported.current) return;
    reported.current = true;
    void consumeToolUsage(config?.toolSlug ?? 'sound-test', '').catch(() => undefined);
  };

  // Leaving the page must silence anything still playing.
  useEffect(
    () => () => {
      stopRef.current?.();
      try {
        toneRef.current?.o.stop();
      } catch {
        // already stopped
      }
    },
    [],
  );

  function stopAll() {
    stopRef.current?.();
    stopRef.current = null;
    if (toneRef.current) {
      const { o, g } = toneRef.current;
      const ctx = o.context;
      g.gain.setTargetAtTime(0, ctx.currentTime, 0.02);
      o.stop(ctx.currentTime + 0.1);
      toneRef.current = null;
    }
    setPlaying(null);
    setSweepHz(null);
  }

  function play(label: string, fn: (ctx: AudioContext) => (() => void) | void, ms: number) {
    report();
    stopAll();
    const ctx = audio();
    const stop = fn(ctx);
    setPlaying(label);
    const timer = window.setTimeout(() => {
      setPlaying((p) => (p === label ? null : p));
      setSweepHz(null);
    }, ms);
    stopRef.current = () => {
      window.clearTimeout(timer);
      if (stop) stop();
    };
  }

  const channel = (label: string, pan: number) => () => play(label, (ctx) => chime(ctx, pan), 1000);

  const phase = (inverted: boolean) => () =>
    play(inverted ? 'Out of phase' : 'In phase', (ctx) => chime(ctx, 0, inverted), 1000);

  const bass = () =>
    play(
      'Bass 30-120 Hz',
      (ctx) => {
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.frequency.setValueAtTime(30, ctx.currentTime);
        o.frequency.exponentialRampToValueAtTime(120, ctx.currentTime + 4);
        g.gain.setValueAtTime(0, ctx.currentTime);
        g.gain.linearRampToValueAtTime(0.5, ctx.currentTime + 0.1);
        g.gain.setValueAtTime(0.5, ctx.currentTime + 3.9);
        g.gain.linearRampToValueAtTime(0, ctx.currentTime + 4);
        o.connect(g).connect(ctx.destination);
        o.start();
        o.stop(ctx.currentTime + 4.05);
        return () => o.stop();
      },
      4100,
    );

  const sweep = (from: number, to: number, secs: number, label: string, level = 0.15) =>
    play(
      label,
      (ctx) => {
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        const t0 = ctx.currentTime;
        o.frequency.setValueAtTime(from, t0);
        o.frequency.exponentialRampToValueAtTime(to, t0 + secs);
        g.gain.setValueAtTime(0, t0);
        g.gain.linearRampToValueAtTime(level, t0 + 0.05);
        g.gain.setValueAtTime(level, t0 + secs - 0.05);
        g.gain.linearRampToValueAtTime(0, t0 + secs);
        o.connect(g).connect(ctx.destination);
        o.start();
        o.stop(t0 + secs + 0.05);
        const id = window.setInterval(() => {
          const e = Math.min(1, (ctx.currentTime - t0) / secs);
          setSweepHz(Math.round(from * (to / from) ** e));
        }, 50);
        window.setTimeout(() => window.clearInterval(id), secs * 1000 + 100);
        return () => {
          window.clearInterval(id);
          try {
            o.stop();
          } catch {
            // already stopped
          }
        };
      },
      secs * 1000 + 100,
    );

  const pan = () =>
    play(
      'Moving left to right',
      (ctx) => {
        const src = ctx.createBufferSource();
        src.buffer = pinkNoise(ctx, 6);
        const p = ctx.createStereoPanner();
        const g = ctx.createGain();
        g.gain.value = 0.6;
        const t = ctx.currentTime;
        p.pan.setValueAtTime(-1, t);
        p.pan.linearRampToValueAtTime(1, t + 3);
        p.pan.linearRampToValueAtTime(-1, t + 6);
        src.connect(g).connect(p).connect(ctx.destination);
        src.start();
        return () => src.stop();
      },
      6000,
    );

  const surround = (index: number, total: number, label: string) => () =>
    play(
      label,
      (ctx) => {
        const n = Math.min(total, ctx.destination.maxChannelCount);
        ctx.destination.channelCount = n;
        ctx.destination.channelInterpretation = 'discrete';
        const merger = ctx.createChannelMerger(n);
        const src = ctx.createBufferSource();
        src.buffer = pinkNoise(ctx, 1.5);
        const g = ctx.createGain();
        g.gain.value = 0.7;
        src.connect(g);
        g.connect(merger, 0, index);
        merger.connect(ctx.destination);
        src.start();
        return () => {
          src.stop();
          ctx.destination.channelCount = 2;
          ctx.destination.channelInterpretation = 'speakers';
        };
      },
      1600,
    );

  useEffect(() => {
    if (variant !== 'speaker-test') return;
    try {
      setChannels(audio().destination.maxChannelCount);
    } catch {
      setChannels(2);
    }
  }, [variant]);

  // Tone generator: one oscillator whose parameters follow the controls live.
  function toggleTone() {
    if (toneRef.current) {
      stopAll();
      return;
    }
    report();
    stopAll();
    const ctx = audio();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    const p = ctx.createStereoPanner();
    o.type = wave;
    o.frequency.value = freq;
    g.gain.value = 0;
    g.gain.setTargetAtTime(vol, ctx.currentTime, 0.02);
    p.pan.value = bal;
    o.connect(g).connect(p).connect(ctx.destination);
    o.start();
    toneRef.current = { o, g, p };
    setPlaying('Tone');
  }
  useEffect(() => {
    const t = toneRef.current;
    if (!t) return;
    const now = t.o.context.currentTime;
    t.o.type = wave;
    t.o.frequency.setTargetAtTime(freq, now, 0.01);
    t.g.gain.setTargetAtTime(vol, now, 0.02);
    t.p.pan.setTargetAtTime(bal, now, 0.02);
  }, [freq, wave, vol, bal]);

  const lr = (
    <div className="tool-live-actions">
      <PlayButton label={'Left'} onClick={channel('Left', -1)} playing={playing} />
      <PlayButton label={'Both'} onClick={channel('Both', 0)} playing={playing} />
      <PlayButton label={'Right'} onClick={channel('Right', 1)} playing={playing} />
    </div>
  );

  const freqSlider = (
    <label className="tool-live-field tool-live-field-full">
      <span>Frequency: {freq.toLocaleString('en-US')} Hz</span>
      <input
        max={Math.log10(22000)}
        min={Math.log10(1)}
        onChange={(e) => setFreq(Math.round(10 ** Number(e.target.value)))}
        step={0.001}
        type="range"
        value={Math.log10(freq)}
      />
    </label>
  );

  const note = (text: string) => <p className="tool-live-panel-note">{text}</p>;

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">Audio</span>
        <h2 className="tool-live-title">{config?.heading ?? 'Sound test'}</h2>
        <p className="tool-live-subtitle">
          Start at a low volume and turn it up. Sounds are generated in your browser; nothing is
          recorded.
        </p>
      </div>
      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          {variant === 'tone-generator' ? (
            <>
              {freqSlider}
              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                <label className="tool-live-field">
                  <span>Frequency (Hz)</span>
                  <input
                    inputMode="decimal"
                    onChange={(e) =>
                      setFreq(Math.min(22000, Math.max(1, Number(e.target.value) || 1)))
                    }
                    type="number"
                    value={freq}
                  />
                </label>
                <label className="tool-live-field">
                  <span>Waveform</span>
                  <select onChange={(e) => setWave(e.target.value as OscillatorType)} value={wave}>
                    <option value="sine">Sine</option>
                    <option value="square">Square</option>
                    <option value="triangle">Triangle</option>
                    <option value="sawtooth">Sawtooth</option>
                  </select>
                </label>
              </div>
              <div className="tool-live-actions">
                {[50, 100, 250, 440, 1000, 5000, 10000, 15000].map((f) => (
                  <button
                    className="button button-secondary"
                    key={f}
                    onClick={() => setFreq(f)}
                    type="button"
                  >
                    {f >= 1000 ? `${f / 1000} kHz` : `${f} Hz`}
                  </button>
                ))}
              </div>
              <label className="tool-live-field tool-live-field-full">
                <span>Volume: {Math.round(vol * 100)}%</span>
                <input
                  max={1}
                  min={0}
                  onChange={(e) => setVol(Number(e.target.value))}
                  step={0.01}
                  type="range"
                  value={vol}
                />
              </label>
              <label className="tool-live-field tool-live-field-full">
                <span>
                  Balance:{' '}
                  {bal === 0
                    ? 'centre'
                    : bal < 0
                      ? `left ${Math.round(-bal * 100)}%`
                      : `right ${Math.round(bal * 100)}%`}
                </span>
                <input
                  max={1}
                  min={-1}
                  onChange={(e) => setBal(Number(e.target.value))}
                  step={0.05}
                  type="range"
                  value={bal}
                />
              </label>
              <div className="tool-live-actions">
                <button className="button" onClick={toggleTone} type="button">
                  {playing === 'Tone' ? 'Stop' : 'Play'}
                </button>
                <button
                  className="button button-secondary"
                  onClick={() => setFreq((f) => Math.max(1, f - 1))}
                  type="button"
                >
                  −1 Hz
                </button>
                <button
                  className="button button-secondary"
                  onClick={() => setFreq((f) => Math.min(22000, f + 1))}
                  type="button"
                >
                  +1 Hz
                </button>
              </div>
            </>
          ) : variant === 'hearing-test' ? (
            <>
              <div className="tool-live-actions">
                <PlayButton
                  label={'High tones: 8 kHz up to 20 kHz'}
                  onClick={() => sweep(8000, 20000, 24, 'High tones: 8 kHz up to 20 kHz', 0.2)}
                  playing={playing}
                />
                <PlayButton
                  label={'Low tones: 100 Hz down to 20 Hz'}
                  onClick={() => sweep(100, 20, 16, 'Low tones: 100 Hz down to 20 Hz', 0.4)}
                  playing={playing}
                />
              </div>
              <button
                className="button"
                disabled={!sweepHz}
                onClick={() => {
                  if (!sweepHz || !playing) return;
                  setHeard((h) =>
                    playing.startsWith('High') ? { ...h, high: sweepHz } : { ...h, low: sweepHz },
                  );
                  stopAll();
                }}
                style={{ fontSize: '1.1rem', padding: '0.9rem' }}
                type="button"
              >
                {sweepHz
                  ? `I can’t hear it any more (${sweepHz.toLocaleString('en-US')} Hz)`
                  : 'Press when the tone disappears'}
              </button>
              {note(
                'Use headphones in a quiet room. Set volume comfortable at the start; do not raise it during the sweep. Stop if anything is uncomfortable.',
              )}
            </>
          ) : (
            <>
              {variant !== 'speaker-test' ? lr : null}
              {variant === 'speaker-test' ? (
                <>
                  <div className="tool-live-actions">
                    {(
                      SURROUND[
                        channels && channels >= 8
                          ? 8
                          : channels && channels >= 6
                            ? 6
                            : channels && channels >= 4
                              ? 4
                              : 2
                      ] ?? SURROUND[2]
                    ).map((name, i, arr) => (
                      <PlayButton
                        key={name}
                        label={name}
                        onClick={surround(i, arr.length, name)}
                        playing={playing}
                      />
                    ))}
                  </div>
                  {lr}
                </>
              ) : null}
              {variant !== 'left-right' ? (
                <div className="tool-live-actions">
                  <PlayButton label={'Bass 30-120 Hz'} onClick={bass} playing={playing} />
                  <PlayButton
                    label={'Sweep 20 Hz to 20 kHz'}
                    onClick={() => sweep(20, 20000, 12, 'Sweep 20 Hz to 20 kHz')}
                    playing={playing}
                  />
                </div>
              ) : null}
              <div className="tool-live-actions">
                <PlayButton label={'Moving left to right'} onClick={pan} playing={playing} />
                {variant !== 'left-right' ? (
                  <>
                    <PlayButton label={'In phase'} onClick={phase(false)} playing={playing} />
                    <PlayButton label={'Out of phase'} onClick={phase(true)} playing={playing} />
                  </>
                ) : null}
              </div>
              {playing ? (
                <button className="button button-secondary" onClick={stopAll} type="button">
                  Stop
                </button>
              ) : null}
            </>
          )}
        </section>
        <section className="tool-live-preview">
          {sweepHz ? (
            <p
              className="tool-live-title"
              style={{ margin: 0, fontVariantNumeric: 'tabular-nums' }}
            >
              {sweepHz.toLocaleString('en-US')} Hz
            </p>
          ) : null}
          {variant === 'hearing-test' ? (
            <>
              <div className="tool-html-meta-grid">
                {[
                  [
                    'Highest tone you heard',
                    heard.high ? `${heard.high.toLocaleString('en-US')} Hz` : '—',
                  ],
                  [
                    'Lowest tone you heard',
                    heard.low ? `${heard.low.toLocaleString('en-US')} Hz` : '—',
                  ],
                ].map(([k, v]) => (
                  <div className="tool-live-field" key={k}>
                    <span>{k}</span>
                    <input readOnly type="text" value={v} />
                  </div>
                ))}
              </div>
              {note(
                'Young adults typically hear up to 17-20 kHz; the upper limit falls with age, to around 12-14 kHz by 50-60. Headphones and speakers often roll off above 16 kHz or below 40 Hz, which limits this test. This is a frequency-range check, not a medical hearing test; see an audiologist about any concern.',
              )}
            </>
          ) : variant === 'tone-generator' ? (
            note(
              'Uses: tuning (A4 = 440 Hz), finding a rattle or a room resonance, testing speakers and subwoofers, tinnitus frequency matching. Keep the volume low for high frequencies; they can be uncomfortable before they sound loud.',
            )
          ) : variant === 'speaker-test' ? (
            <>
              {note(
                channels && channels > 2
                  ? `Your audio output reports ${channels} channels. Each button plays pink noise from one speaker only; if the sound comes from the wrong place, the speaker wires or the receiver’s channel map are swapped.`
                  : 'Your audio output reports stereo (2 channels). To test a 5.1 or 7.1 system, set the output device to surround in your OS sound settings and reload; browsers only expose the channels the system reports.',
              )}
              {note(
                'In phase should sound solid and centred; out of phase sounds hollow and spread. If "in phase" sounds hollow, one speaker has its + and − wires reversed.',
              )}
            </>
          ) : variant === 'left-right' ? (
            note(
              'Left should come only from the left ear or speaker, Right only from the right. If they are swapped, check the headphone orientation and the balance slider in your sound settings.',
            )
          ) : (
            <>
              {note(
                variant === 'headphone-test'
                  ? 'Left and Right check the channels; the moving sound should glide smoothly from ear to ear. The sweep reveals the frequency range and any rattle; the bass test shows how deep your headphones go.'
                  : 'Left, Right and Both check the channels. The sweep plays every frequency from 20 Hz to 20 kHz: gaps, buzzing or rattles point to a fault. Laptop and phone speakers usually start around 150-200 Hz, so the bass test may be silent on them.',
              )}
              {note(
                'In phase should sound solid and centred; out of phase sounds hollow and wide. If "in phase" sounds hollow, one speaker is wired backwards.',
              )}
            </>
          )}
        </section>
      </div>
    </div>
  );
}
